<template>
  <el-form
    :model="loginForm"
    label-position="top"
    ref="widgetLogin"
    id="widget-login-form"
  >
    <template>
      <el-row v-for="(field, index) in schema" :key="index" type="flex">
        <el-col :span="24">
          <component
            class="authx-input"
            :field-types="fieldTypes"
            :is="'text-input'"
            :label="field.label"
            :parentForm="loginForm"
            :loadFieldFrom="'login'"
            :meta-data="field"
            :colors="colors"
            :form-index="index"
            :ref="`input-field-${index}`"
            :value.sync="loginForm[field.name.toLowerCase()]"
            @isValid="isValid"
          />
        </el-col>
      </el-row>
      <el-row type="flex">
        <el-col :span="24" class="form-extras">
          <el-checkbox v-model="loginForm.rememberMe" id="rememberMe"
            >Remember me</el-checkbox
          >
          <span @click="$emit('navigate', 'forgot-password')"
            >Forgot password?</span
          >
        </el-col>
      </el-row>
    </template>
    <el-row type="flex" class="mt-1">
      <el-col :span="24">
        <el-button
          class="authx-button w-100"
          type="primary"
          :loading="loading"
          :disabled="preview"
          @click="login"
          >Login</el-button
        >
      </el-col>
    </el-row>
    <el-row type="flex">
      <el-col :span="24">
        <p class="form-footer">
          Don't have an account?
          <span @click="$emit('navigate', 'signup')">Sign Up</span>
        </p>
      </el-col>
    </el-row>

    <form-social
      :label="'Login with'"
      :google-auth="googleAuth"
      :facebook-auth="facebookAuth"
      :session-token="domainData.sessionToken"
      :colors="colors"
    />
  </el-form>
</template>

<script>
import TextInput from "@/components/Products/AuthX/Widget/FormInputs/TextInput";
import { currentUser, redirectToSocialLogin } from "@/lib/utils";
import FormSocial from "@/components/Products/AuthX/Widget/Forms/FormSocial.vue";

export default {
  name: "FormLogin",
  components: {
    FormSocial,
    TextInput
  },

  props: {
    schema: {
      type: Array,
      required: true
    },
    fieldTypes: {
      type: Array,
      default() {
        return [];
      }
    },
    domainData: {
      type: Object,
      default() {
        return {};
      }
    },
    colors: {
      type: Object,
      default() {
        return {};
      }
    },
    googleAuth: {
      type: Boolean,
      default: false
    },
    facebookAuth: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      radiusUser: currentUser(),
      loginForm: {
        rememberMe: false
      },
      loading: false,
      isValidFrom: true,
      rmCheck: "",
      identifierInput: ""
    };
  },

  inject: ["preview"],

  computed: {
    preview() {
      return this.preview;
    }
  },

  mounted() {
    this.identifierInput = document.querySelector(
      `#widget-login-form #${this.schema[0].name}`
    );
    const vendorAuth = localStorage.vendor && JSON.parse(localStorage.vendor);

    if (vendorAuth && vendorAuth.checkbox) {
      this.loginForm.rememberMe = true;
      this.identifierInput.value = vendorAuth.identifier;
    }

    const checkboxElt = document.querySelector(
      "#rememberMe .el-checkbox__inner"
    );
    checkboxElt.style.borderColor = this.colors.secondary;
  },

  methods: {
    socialLogin(type) {
      if (this.socialType || this.radiusUser) {
        return;
      }

      this.socialType = type;
      localStorage.setItem("vendor-social-type", type);

      redirectToSocialLogin(this.$http, type, this.domainData.sessionToken)
        .then(response => {
          const resData = response.data;
          if (resData.status === "error") {
            this.socialType = "";
            this.handleErrors(resData);
          } else {
            localStorage.setItem(
              "vendor-session-token",
              this.domainData.sessionToken
            );
            let redirectUrl = resData.redirect_url;
            if (this.domainData.authxState) {
              redirectUrl += `&__authx_state=${this.domainData.authxState}`;
            }
            window.location = redirectUrl;
          }
        })
        .catch(err => {
          this.socialType = "";
          this.notification("Error", err.message, "danger");
        });
    },

    isRememberMe() {
      const vendorAuth = { identifier: "", checkbox: "" };
      if (this.loginForm.rememberMe) {
        vendorAuth.identifier = this.loginForm[this.schema[0].name];
        vendorAuth.checkbox = this.loginForm.rememberMe;
      }

      localStorage.setItem("vendor", JSON.stringify(vendorAuth));
    },

    isValid(val) {
      this.isValidFrom = val;
    },

    login() {
      this.loading = true;
      this.isValidFrom = true;
      this.schema.forEach((item, i) => {
        this.$refs[`input-field-${i}`][0].validateInput();
      });

      if (!this.isValidFrom || this.radiusUser) {
        this.loading = false;
        return false;
      }

      const identifier = this.loginForm[this.schema[0].name];
      const password = this.loginForm[this.schema[1].name];

      this.isRememberMe();

      this.$http
        .post(
          "apps/password/auth",
          {
            body: {
              identifier: identifier,
              password: password
            },
            method: "POST"
          },
          {
            headers: {
              "Session-Token": this.domainData.sessionToken
            }
          }
        )
        .then(response => {
          this.loading = false;
          const resData = response.data;
          if (resData.status === "error") {
            this.handleErrors(resData);
            this.gaTrack("Failed");
          } else {
            this.$emit("next", {
              action: resData.action,
              identifier: identifier,
              token: resData.data.token ?? null,
              redirect_uri: resData.data.redirect_uri ?? null
            });
            this.gaTrack("Successful", resData.data.action);
          }
        })
        .catch(() => {
          this.loading = false;
          this.notification("Error", "An unknown error occurred", "danger");
        });
    },

    redirectApp(data) {
      setTimeout(() => {
        this.notification(
          "Logged In",
          "User logged in successfully",
          "success"
        );
        let { redirect_uri } = data;
        if (this.domainMetaData.authxState) {
          redirect_uri += `&__authx_state=${this.domainMetaData.authxState}`;
        }
        window.location = redirect_uri;
      }, 1000);
    },

    gaTrack(type, action) {
      this.$ga.event({
        eventCategory: "AuthX Widget",
        eventAction: `Login`,
        eventLabel: type,
        eventValue: action ? action : "-"
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.form-extras {
  display: flex;
  justify-content: space-between;
  align-items: center;

  .el-checkbox__label {
    font-size: 0.875rem;
    font-weight: normal;
  }

  span {
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    text-decoration: underline;
  }
}
</style>
